const YOUTUBE_HOSTS = ['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtu.be']

/**
 * Returns an error message if the given string doesn't look like a valid http(s) URL.
 * Returns null if the string is empty (let required-field logic handle empties) or valid.
 */
export function getUrlLikenessError(url: string): string | null {
  const trimmed = url.trim()
  if (!trimmed) return null
  // `new URL()` silently encodes/strips internal whitespace, so reject up front.
  if (/\s/.test(trimmed)) {
    return 'URL cannot contain spaces or line breaks'
  }
  try {
    const parsed = new URL(trimmed)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return 'URL must start with http:// or https://'
    }
    // Rejects localhost, IPv6 literals, and bare hostnames like `https://foo` that
    // the backend cannot fetch from. IPv4 addresses naturally have dots and pass.
    if (!parsed.hostname.includes('.')) {
      return 'URL must point to a public domain (e.g. https://example.com)'
    }
    return null
  } catch {
    return "That doesn't look like a valid URL. Make sure it starts with https:// (e.g. https://example.com)"
  }
}

/**
 * Returns an error message if the URL is a YouTube playlist, channel, or other non-video page.
 * Returns null if the URL is fine (either not YouTube, or a valid video URL).
 */
export function getYouTubeUrlError(url: string): string | null {
  try {
    const parsed = new URL(url)
    const host = parsed.hostname.toLowerCase()

    if (!YOUTUBE_HOSTS.includes(host)) return null

    if (parsed.pathname === '/playlist') {
      return 'YouTube playlist URLs are not supported. Please provide a direct video URL (e.g. youtube.com/watch?v=...).'
    }

    if (
      parsed.pathname.startsWith('/@') ||
      parsed.pathname.startsWith('/channel/') ||
      parsed.pathname.startsWith('/c/')
    ) {
      return 'YouTube channel URLs are not supported. Please provide a direct video URL.'
    }

    if (parsed.pathname === '/watch' && parsed.searchParams.has('list') && !parsed.searchParams.has('v')) {
      return 'This appears to be a playlist URL. Please provide a direct video URL instead.'
    }

    return null
  } catch {
    return null
  }
}
